import React, { useEffect, useState } from 'react';
import {
  BarChart3, Plus, X, Loader, CheckCircle, AlertCircle, Target,
  MousePointerClick, ShoppingBag, TrendingUp, Copy, Link2, RefreshCw
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell
} from 'recharts';
import { getAttributionDashboard, createCampaign } from '../services/api';
import './AttributionDashboard.css';

const fmt = (n) => (n || n === 0 ? new Intl.NumberFormat('fr-FR').format(n) + ' XOF' : '—');
const fmtNum = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);

const BAR_COLORS = ['#7c3aed', '#2563eb', '#16a34a', '#d97706', '#dc2626', '#0891b2', '#db2777'];
const EMPTY = { nom: '', promoCode: '', targetUrl: '', attributionWindowHours: 72 };

const STATUS = {
  active: { label: 'Active', color: '#16a34a', bg: '#dcfce7' },
  paused: { label: 'En pause', color: '#d97706', bg: '#fef3c7' },
  ended:  { label: 'Terminée', color: '#475569', bg: '#f1f5f9' },
};

export default function AttributionDashboard() {
  const [data, setData] = useState({ lignes: [], totaux: { clics: 0, ventes: 0, ca: 0 } });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const showToast = (type, text) => { setToast({ type, text }); setTimeout(() => setToast(null), 3500); };

  const load = async () => {
    try {
      setLoading(true);
      const res = await getAttributionDashboard();
      setData(res.data.data || { lignes: [], totaux: { clics: 0, ventes: 0, ca: 0 } });
    } catch { showToast('error', 'Erreur de chargement du dashboard'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await createCampaign({
        nom: form.nom,
        promoCode: form.promoCode || null,
        targetUrl: form.targetUrl || '',
        attributionWindowHours: Number(form.attributionWindowHours) || 72,
      });
      showToast('success', 'Campagne créée');
      setShowModal(false); setForm(EMPTY); load();
      if (res.data.data?.trackedUrl) navigator.clipboard?.writeText(res.data.data.trackedUrl).catch(() => {});
    } catch (err) {
      showToast('error', err.response?.data?.message || err.response?.data?.errors?.[0]?.message || 'Erreur');
    } finally { setSubmitting(false); }
  };

  const copy = (txt) => { navigator.clipboard?.writeText(txt); showToast('success', 'Lien copié'); };

  const totalTaux = data.totaux.clics > 0
    ? Math.round((data.totaux.ventes / data.totaux.clics) * 1000) / 10 : null;

  const chartData = data.lignes
    .filter((l) => l.ca > 0)
    .map((l) => ({ nom: l.campagne.nom, ca: l.ca }));

  return (
    <div className="at-page">
      {toast && (
        <div className={`at-toast at-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <span>{toast.text}</span>
        </div>
      )}

      <div className="at-header">
        <div>
          <h1 className="at-title"><BarChart3 size={26} /> Attribution des campagnes</h1>
          <p className="at-subtitle">Ventes et CA générés par campagne — code promo, lien tracké, fenêtre temporelle.</p>
        </div>
        <div className="at-header-actions">
          <button className="at-btn at-btn--ghost" onClick={load} title="Rafraîchir"><RefreshCw size={16} /></button>
          <button className="at-btn at-btn--primary" onClick={() => setShowModal(true)}><Plus size={18} /> Nouvelle campagne</button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="at-kpis">
        <div className="at-kpi"><div className="at-kpi-ic" style={{ background: '#ede9fe', color: '#7c3aed' }}><MousePointerClick size={20} /></div>
          <div><div className="at-kpi-val">{fmtNum(data.totaux.clics)}</div><div className="at-kpi-lbl">Clics</div></div></div>
        <div className="at-kpi"><div className="at-kpi-ic" style={{ background: '#dbeafe', color: '#2563eb' }}><ShoppingBag size={20} /></div>
          <div><div className="at-kpi-val">{fmtNum(data.totaux.ventes)}</div><div className="at-kpi-lbl">Ventes attribuées</div></div></div>
        <div className="at-kpi"><div className="at-kpi-ic" style={{ background: '#dcfce7', color: '#16a34a' }}><TrendingUp size={20} /></div>
          <div><div className="at-kpi-val">{fmt(data.totaux.ca)}</div><div className="at-kpi-lbl">CA généré</div></div></div>
        <div className="at-kpi"><div className="at-kpi-ic" style={{ background: '#fef3c7', color: '#d97706' }}><Target size={20} /></div>
          <div><div className="at-kpi-val">{totalTaux != null ? `${totalTaux}%` : '—'}</div><div className="at-kpi-lbl">Taux de conversion</div></div></div>
      </div>

      {loading ? (
        <div className="at-loading"><Loader className="at-spin" size={28} /> Chargement…</div>
      ) : data.lignes.length === 0 ? (
        <div className="at-empty"><BarChart3 size={42} /><p>Aucune campagne. Créez-en une pour suivre vos ventes.</p>
          <button className="at-btn at-btn--primary" onClick={() => setShowModal(true)}><Plus size={16} /> Nouvelle campagne</button>
        </div>
      ) : (
        <>
          {/* Chart */}
          {chartData.length > 0 && (
            <div className="at-card at-chart">
              <h3>CA généré par campagne</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                  <XAxis dataKey="nom" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tickFormatter={(v) => new Intl.NumberFormat('fr-FR', { notation: 'compact' }).format(v)} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip formatter={(v) => fmt(v)} cursor={{ fill: '#f8fafc' }} />
                  <Bar dataKey="ca" radius={[6, 6, 0, 0]}>
                    {chartData.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Table */}
          <div className="at-card at-table-wrap">
            <table className="at-table">
              <thead>
                <tr><th>Campagne</th><th>Statut</th><th>Lien tracké</th><th>Clics</th><th>Ventes</th><th>CA</th><th>Taux</th></tr>
              </thead>
              <tbody>
                {data.lignes.map((l) => (
                  <tr key={l.campagne.id}>
                    <td>
                      <div className="at-camp-nom">{l.campagne.nom}</div>
                      {l.campagne.promoCode && <span className="at-promo">code {l.campagne.promoCode}</span>}
                    </td>
                    <td><span className="at-badge" style={{ color: STATUS[l.campagne.status]?.color, background: STATUS[l.campagne.status]?.bg }}>{STATUS[l.campagne.status]?.label || l.campagne.status}</span></td>
                    <td>
                      {l.campagne.trackedUrl ? (
                        <button className="at-link" onClick={() => copy(l.campagne.trackedUrl)} title={l.campagne.trackedUrl}>
                          <Link2 size={13} /> /t/{l.campagne.trackedCode} <Copy size={12} />
                        </button>
                      ) : '—'}
                    </td>
                    <td>{fmtNum(l.clics)}</td>
                    <td>{fmtNum(l.ventes)}</td>
                    <td className="at-money">{fmt(l.ca)}</td>
                    <td>{l.tauxConversion != null ? `${l.tauxConversion}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="at-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="at-modal" onClick={(e) => e.stopPropagation()}>
            <div className="at-modal-head">
              <h3><Target size={20} /> Nouvelle campagne</h3>
              <button className="at-icon-btn" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleCreate} className="at-form">
              <label>Nom *
                <input value={form.nom} required onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Promo Tabaski" />
              </label>
              <label>Code promo (optionnel)
                <input value={form.promoCode} onChange={(e) => setForm({ ...form, promoCode: e.target.value })} placeholder="TABA24" />
              </label>
              <label>URL de destination (lien tracké)
                <input value={form.targetUrl} onChange={(e) => setForm({ ...form, targetUrl: e.target.value })} placeholder="https://… (vitrine, produit)" />
              </label>
              <label>Fenêtre d'attribution (heures)
                <input type="number" min="1" value={form.attributionWindowHours} onChange={(e) => setForm({ ...form, attributionWindowHours: e.target.value })} />
              </label>
              <p className="at-hint">Le lien tracké est généré automatiquement et copié dans le presse-papier.</p>
              <div className="at-modal-foot">
                <button type="button" className="at-btn at-btn--ghost" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="at-btn at-btn--primary" disabled={submitting}>
                  {submitting ? <Loader className="at-spin" size={16} /> : <Plus size={16} />} Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
